import { loginUrl, useLiveEvents, useMe } from '@masar/api-client/query';
import { useI18n } from '@masar/i18n/react';
import { Button, EmptyState, ErrorNote, PageHeader, SkeletonList } from '@masar/ui';
import { lazy, Suspense, useEffect, type ComponentType, type LazyExoticComponent } from 'react';
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  RouterProvider,
  useLocation,
  useRouteError,
} from 'react-router';
import { Shell } from '../shell/Shell';
import { useBookingDrawer } from './booking-drawer';
import { NAV } from './nav';
import { hasAny, type Perm } from './permissions';
import { MeProvider, useMeStrict } from './session';

const LoginPage = lazy(() => import('../pages/auth/LoginPage'));
const InvitePage = lazy(() => import('../pages/auth/InvitePage'));
const BookingDrawer = lazy(() => import('../pages/desk/BookingDrawer'));

const pages = {
  today: lazy(() => import('../pages/desk/TodayPage')),
  calendar: lazy(() => import('../pages/desk/CalendarPage')),
  bookings: lazy(() => import('../pages/desk/BookingsPage')),
  walkin: lazy(() => import('../pages/desk/WalkInPage')),
  handover: lazy(() => import('../pages/counter/HandoverPage')),
  return: lazy(() => import('../pages/counter/ReturnPage')),
  customers: lazy(() => import('../pages/customers/CustomersPage')),
  customer: lazy(() => import('../pages/customers/CustomerPage')),
  credits: lazy(() => import('../pages/customers/CreditsPage')),
  overview: lazy(() => import('../pages/insights/OverviewPage')),
  finance: lazy(() => import('../pages/insights/FinancePage')),
  reports: lazy(() => import('../pages/insights/ReportsPage')),
  content: lazy(() => import('../pages/insights/ContentPage')),
  fleet: lazy(() => import('../pages/setup/FleetPage')),
  team: lazy(() => import('../pages/setup/TeamPage')),
  pricing: lazy(() => import('../pages/setup/PricingPage')),
} satisfies Record<string, LazyExoticComponent<ComponentType>>;

function Loading() {
  return (
    <div style={{ padding: 32 }}>
      <SkeletonList rows={4} />
    </div>
  );
}

/** Pages the member may not open show this instead (the API refuses them anyway). */
function Forbidden() {
  const { t } = useI18n();
  return (
    <>
      <PageHeader title={t('لا تملك صلاحية هذه الصفحة')} />
      <EmptyState>{t('لا توجد صفحات مسموحة لهذا الحساب. تواصل مع المالك.')}</EmptyState>
    </>
  );
}

function Guard({ perms, page: Page }: { perms: readonly Perm[]; page: ComponentType }) {
  const me = useMeStrict();
  if (!hasAny(me.permissions, perms)) return <Forbidden />;
  return <Page />;
}

function Home() {
  const me = useMeStrict();
  const first = NAV.find((n) => hasAny(me.permissions, n.perms));
  if (!first) return <Forbidden />;
  return <Navigate to={first.path} replace />;
}

function DrawerHost() {
  const { id, close } = useBookingDrawer();
  if (!id) return null;
  return (
    <Suspense fallback={null}>
      <BookingDrawer id={id} onClose={close} />
    </Suspense>
  );
}

/** Signed-in area: GET /auth/me, or off to the sign-in page. */
function Authed() {
  const { t } = useI18n();
  const me = useMe();
  const loc = useLocation();
  useLiveEvents({ enabled: Boolean(me.data) });

  useEffect(() => {
    if (me.data === null) {
      window.location.assign(loginUrl(loc.pathname + loc.search));
    }
  }, [me.data, loc.pathname, loc.search]);

  if (me.isPending || me.data === null) return <Loading />;
  if (me.isError) {
    return (
      <div style={{ padding: 32, display: 'grid', gap: 16, justifyItems: 'start' }}>
        <ErrorNote error={me.error} />
        <Button onClick={() => void me.refetch()}>{t('إعادة المحاولة')}</Button>
      </div>
    );
  }
  return (
    <MeProvider me={me.data}>
      <Shell>
        <Suspense fallback={<SkeletonList rows={4} />}>
          <Outlet />
        </Suspense>
      </Shell>
      <DrawerHost />
    </MeProvider>
  );
}

function RouteError() {
  const { t } = useI18n();
  const err = useRouteError();
  return (
    <div style={{ padding: 32, display: 'grid', gap: 16, justifyItems: 'start' }}>
      <h1 style={{ fontSize: 24 }}>{t('حدث خطأ غير متوقع')}</h1>
      <ErrorNote error={err} />
      <Button onClick={() => window.location.reload()}>{t('إعادة تحميل الصفحة')}</Button>
    </div>
  );
}

function NotFound() {
  const { t } = useI18n();
  return (
    <>
      <PageHeader title={t('الصفحة غير موجودة')} />
      <EmptyState>{t('تحقق من الرابط أو اختر صفحة من القائمة.')}</EmptyState>
    </>
  );
}

const g = (perms: readonly Perm[], page: ComponentType) => <Guard perms={perms} page={page} />;

const router = createBrowserRouter([
  {
    errorElement: <RouteError />,
    children: [
      {
        path: '/login',
        element: (
          <Suspense fallback={<Loading />}>
            <LoginPage />
          </Suspense>
        ),
      },
      {
        path: '/invite',
        element: (
          <Suspense fallback={<Loading />}>
            <InvitePage />
          </Suspense>
        ),
      },
      {
        element: <Authed />,
        children: [
          { index: true, element: <Home /> },
          { path: 'today', element: g(['p.today'], pages.today) },
          { path: 'calendar', element: g(['p.calendar'], pages.calendar) },
          { path: 'bookings', element: g(['p.calendar'], pages.bookings) },
          { path: 'walk-in', element: g(['p.walkin'], pages.walkin) },
          { path: 'handover', element: g(['p.checkin'], pages.handover) },
          { path: 'handover/:bookingId', element: g(['p.checkin'], pages.handover) },
          { path: 'return', element: g(['p.checkout'], pages.return) },
          { path: 'return/:bookingId', element: g(['p.checkout'], pages.return) },
          { path: 'customers', element: g(['p.calendar', 'p.vouchers'], pages.customers) },
          { path: 'customers/:customerId', element: g(['p.calendar', 'p.vouchers'], pages.customer) },
          { path: 'credits', element: g(['p.vouchers'], pages.credits) },
          { path: 'overview', element: g(['p.overview'], pages.overview) },
          { path: 'finance', element: g(['p.finance'], pages.finance) },
          { path: 'reports/*', element: g(['p.reports'], pages.reports) },
          { path: 'content', element: g(['p.content'], pages.content) },
          { path: 'fleet', element: g(['p.fleet'], pages.fleet) },
          { path: 'team/*', element: g(['p.team'], pages.team) },
          { path: 'pricing/*', element: g(['p.pricing'], pages.pricing) },
          { path: '*', element: <NotFound /> },
        ],
      },
    ],
  },
]);

export function App() {
  return <RouterProvider router={router} />;
}
